import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { Search, X, Globe, Instagram, Youtube, Film, CheckCircle2 } from "lucide-react";
import { ProfileAvatar } from "@/components/build/ProfileAvatar";
import { WorkSampleThumb, WorkSampleLightbox } from "@/components/WorkSampleViewer";
import "./partnerWorkspace.css";

/**
 * Freelancer Discovery Engine, Phase 2A (2026-08) — the magic-link Partner Workspace's
 * "Discover Talent" tab. Calls the SAME `GET /v1/build/discovery` every freelancer/NJM staff
 * member's Members directory calls — server-narrowed to `visible_to_partners` (opt-out,
 * default ON) and ALWAYS the zero-rate/zero-contact public card. This page never renders a
 * richer shape than that response; there is no separate partner-only field anywhere here.
 */
function ChipInput({ testId, placeholder, terms, onChange }) {
  const [draft, setDraft] = useState("");
  const commit = () => {
    const t = draft.trim();
    if (t && !terms.includes(t)) onChange([...terms, t]);
    setDraft("");
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-xl px-3 py-2 min-w-[200px]" style={{ border: "1px solid var(--pw-border-subtle)", background: "var(--pw-surface)" }} data-testid={testId}>
      {terms.map((t) => (
        <span key={t} className="pw-pill inline-flex items-center gap-1" style={{ background: "var(--pw-warm-tint)", color: "var(--pw-text-primary)" }}>
          {t}
          <button onClick={() => onChange(terms.filter((x) => x !== t))} data-testid={`${testId}-remove-${t}`}><X className="h-2.5 w-2.5" /></button>
        </span>
      ))}
      <input value={draft} onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(); } }}
        onBlur={commit} placeholder={terms.length ? "" : placeholder} data-testid={`${testId}-input`}
        className="flex-1 min-w-[100px] bg-transparent text-[13px] focus:outline-none py-0.5" style={{ color: "var(--pw-text-primary)" }} />
    </div>
  );
}

function initialsOf(name) {
  return (name || "?").trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
}

function TalentCard({ m, expanded, onToggle }) {
  const skills = (m.skills || "").split(",").map((s) => s.trim()).filter(Boolean).slice(0, expanded ? undefined : 4);
  const [viewingWork, setViewingWork] = useState(null);
  const links = [
    { key: "website", icon: Globe, url: m.website },
    { key: "instagram", icon: Instagram, url: m.instagram },
    { key: "youtube", icon: Youtube, url: m.youtube },
    { key: "vimeo", icon: Film, url: m.vimeo },
  ].filter((l) => l.url);
  return (
    <div className="pw-card p-5" data-testid={`partner-talent-card-${m.id}`}>
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-full flex items-center justify-center text-[13px] font-extrabold shrink-0 overflow-hidden" style={{ background: "var(--pw-ink)", color: "var(--pw-yellow)" }}>
          <ProfileAvatar freelancerId={m.id} hasPhoto={Boolean(m.profile_image)} alt="" className="h-full w-full object-cover" testId={`partner-talent-avatar-${m.id}`} fallback={initialsOf(m.name)} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-bold text-[14px] truncate" style={{ color: "var(--pw-text-primary)" }} data-testid={`partner-talent-name-${m.id}`}>{m.name}</span>
            {m.njm_verified && <CheckCircle2 className="h-3.5 w-3.5" style={{ color: "var(--pw-yellow)" }} />}
          </div>
          <div className="text-[12px] mt-0.5" style={{ color: "var(--pw-text-muted)" }}>
            {m.primary_role}{m.canonical_location ? ` · ${m.canonical_location}` : ""}
          </div>
        </div>
      </div>

      {m.bio && <p className={`mt-3 text-[12.5px] leading-relaxed ${expanded ? "" : "line-clamp-3"}`} style={{ color: "var(--pw-text-secondary)" }}>{m.bio}</p>}

      {skills.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {skills.map((s) => <span key={s} className="pw-pill" style={{ background: "var(--pw-warm-tint)", color: "var(--pw-text-secondary)" }}>{s}</span>)}
        </div>
      )}

      {expanded && (m.work_samples || []).length > 0 && (
        <div className="mt-3 pt-3 grid grid-cols-3 gap-2" style={{ borderTop: "1px solid var(--pw-divider)" }} data-testid={`partner-talent-samples-${m.id}`}>
          {m.work_samples.map((s) => (
            <WorkSampleThumb key={s.id} sample={s} onOpen={setViewingWork} label={m.primary_role} testId={`partner-talent-sample-${m.id}-${s.id}`} />
          ))}
        </div>
      )}
      {viewingWork && <WorkSampleLightbox sample={viewingWork} onClose={() => setViewingWork(null)} />}

      {links.length > 0 && (
        <div className="mt-3 flex items-center gap-2">
          {links.map((l) => (
            <a key={l.key} href={l.url} target="_blank" rel="noopener noreferrer" className="h-7 w-7 rounded-full flex items-center justify-center transition-colors" style={{ border: "1px solid var(--pw-border-subtle)", color: "var(--pw-text-muted)" }}>
              <l.icon className="h-3.5 w-3.5" />
            </a>
          ))}
        </div>
      )}

      <button onClick={onToggle} data-testid={`partner-talent-toggle-${m.id}`} className="mt-3 pt-3 text-[11px] font-bold uppercase tracking-wide" style={{ borderTop: "1px solid var(--pw-divider)", color: "var(--pw-info-text)" }}>
        {expanded ? "Show less" : "View Profile →"}
      </button>
    </div>
  );
}

export default function PartnerWorkspaceDiscover() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleOptions, setRoleOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [roleFilter, setRoleFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [expertiseTerms, setExpertiseTerms] = useState([]);
  const [tagTerms, setTagTerms] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    api.get("/v1/build/roles").then((r) => setRoleOptions(r.data.items || [])).catch((e) => console.error("roles fetch failed", e));
    api.get("/v1/build/locations").then((r) => setCityOptions(r.data.items || [])).catch((e) => console.error("locations fetch failed", e));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (roleFilter) params.role = roleFilter;
    if (cityFilter) params.city = cityFilter;
    if (expertiseTerms.length) params.expertise = expertiseTerms.join(",");
    if (tagTerms.length) params.tag = tagTerms.join(",");
    const t = setTimeout(() => {
      api.get("/v1/build/discovery", { params }).then((r) => setItems(r.data.items || [])).catch(() => setItems([])).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [roleFilter, cityFilter, expertiseTerms, tagTerms]);

  const hasFilters = roleFilter || cityFilter || expertiseTerms.length > 0 || tagTerms.length > 0;

  return (
    <div className="pw-theme px-6 py-6 space-y-6" data-testid="partner-workspace-discover-page">
      <p className="text-[13px]" style={{ color: "var(--pw-text-muted)" }}>
        Browse NJM's verified freelancer network for your own productions. You'll only ever see public profile info — no contact details, no rates. Reach out to NJM to engage anyone you find here.
      </p>

      <div className="flex flex-wrap items-center gap-2.5">
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="partner-workspace-discover-role-filter"
          className="rounded-xl px-3.5 py-2 text-[13px]" style={{ border: "1px solid var(--pw-border-subtle)", background: "var(--pw-surface)", color: "var(--pw-text-primary)" }}>
          <option value="">All roles</option>
          {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} data-testid="partner-workspace-discover-city-filter"
          className="rounded-xl px-3.5 py-2 text-[13px]" style={{ border: "1px solid var(--pw-border-subtle)", background: "var(--pw-surface)", color: "var(--pw-text-primary)" }}>
          <option value="">All cities</option>
          {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <ChipInput testId="partner-workspace-discover-expertise-filter" placeholder="Expertise (e.g. drone, colour grading)" terms={expertiseTerms} onChange={setExpertiseTerms} />
        <ChipInput testId="partner-workspace-discover-tag-filter" placeholder="Work tag (e.g. wedding, TVC)" terms={tagTerms} onChange={setTagTerms} />
      </div>

      {loading ? (
        <div className="text-[13px]" style={{ color: "var(--pw-text-muted)" }} data-testid="partner-workspace-discover-loading">Searching the roster…</div>
      ) : items.length === 0 ? (
        <div className="pw-card p-8 text-center text-[13px]" style={{ color: "var(--pw-text-muted)" }} data-testid="partner-workspace-discover-empty">
          {hasFilters ? "No members match these filters." : "No members visible yet."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((m) => (
            <TalentCard key={m.id} m={m} expanded={expandedId === m.id} onToggle={() => setExpandedId(expandedId === m.id ? null : m.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
